import type { InputHTMLAttributes, ReactNode, Ref } from "react";
import { fieldErrorId } from "./field";
import "./field.css";
import "./file-field.css";

// Wired by the component itself; `type` is always "file".
type Wiring = "id" | "type" | "required" | "className" | "aria-invalid" | "aria-describedby" | "children";

type Own = {
  /** id of the input; the label points to it, the hint is `${id}-hint` and the message `${id}-error`. */
  id: string;
  label: ReactNode;
  /** Sets `required` on the input and draws a `*` after the label (hidden from assistive tech). */
  required?: boolean;
  /** Label of the visible trigger. The real input lies transparent over it, so this never enters the
      control's accessible name — the field label alone names it. */
  buttonLabel?: string;
  /** Under the control: the constraint, then the chosen file. Visible at rest, so the input is always
      described by it, and `role="status"` announces it when the file changes. */
  hint?: ReactNode;
  /** Message shown under the hint while `invalid`. Always rendered (hidden otherwise), so its text is in the HTML. */
  error?: string;
  /** Red border, `aria-invalid` on the input, and the message added to `aria-describedby`. */
  invalid?: boolean;
  className?: string;
  /** The form reads the chosen file from it (and focuses it when the field is the first one in error). */
  inputRef?: Ref<HTMLInputElement>;
};

export type FileFieldProps = Own & Omit<InputHTMLAttributes<HTMLInputElement>, keyof Own | Wiring>;

/** Stable id of a file field's hint. */
export const fileFieldHintId = (id: string) => `${id}-hint`;

function Upload() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 10.5V2.5" />
      <path d="M4.5 6L8 2.5L11.5 6" />
      <path d="M2.5 10.5v2a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-2" />
    </svg>
  );
}

/**
 * Labelled file input, wired like ds/Field (label, required mark, hint, error message).
 *
 * The native input stays in the page — transparent, stretched over the box, its own file-selector button
 * stretched with it — instead of being hidden behind a <label>: the control keeps the keyboard focus, opens
 * the picker on Enter or Space, and is announced as a file input. The box under it is decoration.
 */
export default function FileField({
  id,
  label,
  required = false,
  buttonLabel = "Choose file",
  hint,
  error,
  invalid = false,
  className,
  inputRef,
  ...rest
}: FileFieldProps) {
  const hintId = fileFieldHintId(id);
  const errorId = fieldErrorId(id);
  // The hint is on screen at rest; the message only while it is shown (a hidden one would describe nothing).
  const describedBy = [hint ? hintId : null, invalid && error ? errorId : null].filter(Boolean).join(" ");

  return (
    <div className={`ds-field ds-filefield${invalid ? " ds-field--invalid" : ""}${className ? ` ${className}` : ""}`}>
      <label className="ds-field__label" htmlFor={id}>
        {label}
        {required && (
          <span className="ds-field__required" aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </label>
      <span className="ds-filefield__control">
        <input
          {...rest}
          ref={inputRef}
          id={id}
          type="file"
          required={required}
          className="ds-filefield__input"
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy || undefined}
        />
        <span className="ds-filefield__button" aria-hidden="true">
          <span className="ds-filefield__icon">
            <Upload />
          </span>
          {buttonLabel}
        </span>
      </span>
      {hint && (
        <p className="ds-filefield__hint" id={hintId} role="status">
          {hint}
        </p>
      )}
      {error && (
        <p className="ds-field__error" id={errorId} hidden={!invalid}>
          {error}
        </p>
      )}
    </div>
  );
}
